import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { FaToggleOn, FaToggleOff } from "react-icons/fa6";
import { listProjectsApi, startSeoCheckApi } from "../../../apis/projects.api";
import { getCreditBalanceApi } from "../../../apis/payments.api";
import { useDarkMode } from "../../../hooks/useDarkMode";

export default function ProjectStatus() {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [remainingCredits, setRemainingCredits] = useState(null);

  // Load active status from localStorage to persist between visits
  const loadActiveMap = () => {
    try {
      const raw = localStorage.getItem("activeProjects");
      if (!raw) return {};
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  };

  const saveActiveMap = (map) => {
    try {
      localStorage.setItem("activeProjects", JSON.stringify(map));
      // Notify other parts of the app (same tab) that active projects changed
      window.dispatchEvent(
        new CustomEvent("activeProjectsUpdated", { detail: map }),
      );
    } catch {}
  };

  // Fetch projects and credits
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [projectsRes, creditRes] = await Promise.all([
          listProjectsApi(),
          getCreditBalanceApi().catch(() => null),
        ]);

        const activeMap = loadActiveMap();
        const apiProjects = projectsRes.data?.projects || [];
        const mapped = apiProjects.map((p) => ({
          id: p.project_id,
          name: p.project_name,
          domain: p.url,
          isActive: Boolean(activeMap[p.project_id]),
          lastRank: p.last_rank,
          interval:
            p.interval_value && p.interval_unit
              ? `${p.interval_value} ${p.interval_unit}`
              : null,
          nextCheck: p.next_check_time,
          remaining_credits: p.remaining_credits,
        }));

        setProjects(mapped);
        // Prefer credit balance API; fallback to first project's remaining_credits
        if (creditRes?.data) {
          setRemainingCredits(creditRes.data.remaining_credits);
        } else if (apiProjects.length) {
          setRemainingCredits(apiProjects[0].remaining_credits ?? null);
        }
      } catch (err) {
        const message =
          err.response?.data?.message ||
          err.message ||
          "Failed to load projects";
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleToggleSeoCheck = async (projectId, isCurrentlyActive) => {
    // If already active, this click deactivates without any credit deduction or API call
    if (isCurrentlyActive) {
      const nextState = loadActiveMap();
      nextState[projectId] = false;
      saveActiveMap(nextState);
      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, isActive: false } : p)),
      );
      return;
    }

    // Get project details for navigation
    const project = projects.find((p) => p.id === projectId);

    // Navigate immediately to SEO results page
    navigate("/dashboard/seo-result", {
      state: {
        projectId: projectId,
        projectName: project?.name || "Project",
        url: project?.domain || "",
        keyword: "SEO Analysis",
      },
    });

    // Start the SEO check in the background (don't wait for response)
    startSeoCheckApi(projectId)
      .then((res) => {
        // Mark project as active and persist
        const activeMap = loadActiveMap();
        activeMap[projectId] = true;
        saveActiveMap(activeMap);

        if (res.data?.remaining_credits !== undefined) {
          setRemainingCredits(res.data.remaining_credits);
        }
      })
      .catch((err) => {
        // Error handling happens on the SeoResult page
      });
  };

  return (
    <>
    <section>
  
<p className="sr-only">
Seo Up Rank AI SEO platform official website https://novarsis.ai/
provides SEO audits, keyword insights, and website optimization tools.
</p>

{/* page content */}

</section>
    <div
      className={`mx-auto my-10 w-full max-w-5xl rounded-2xl shadow ${isDarkMode ? "bg-gray-800" : "bg-gray-50"} px-4 py-5 sm:px-6 sm:py-6`}
    >
      <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
        <button
          onClick={() => navigate(-1)}
          className={`p-2 rounded-lg transition-colors duration-200 shadow-sm ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-white"}`}
        >
          <FaArrowLeft
            className={`text-xl ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          />
        </button>
        <div>
          <h1
            className={`text-2xl font-bold sm:text-3xl ${isDarkMode ? "text-white" : "text-gray-800"}`}
          >
            Project Status
          </h1>
          <p
            className={`mt-1 text-sm sm:text-base ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            Linked domains are shown here with their current project status
            (active / disabled).
          </p>
        </div>
      </div>
      {remainingCredits !== null && (
        <p
          className={`mb-4 text-sm font-semibold sm:text-base ${isDarkMode ? "text-blue-400" : "text-blue-700"}`}
        >
          Remaining Credits: {remainingCredits}
        </p>
      )}

      {error && (
        <div
          className={`mb-4 border-l-4 p-3 rounded text-sm ${isDarkMode ? "bg-red-900/30 border-red-600 text-red-300" : "bg-red-50 border-red-600 text-red-700"}`}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div
          className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          Loading projects...
        </div>
      ) : (
        <div className="space-y-4">
          {projects.map((p) => (
            <div
              key={p.id}
              className={`flex flex-col gap-4 rounded-lg shadow p-4 sm:flex-row sm:items-center sm:justify-between ${isDarkMode ? "bg-gray-700" : "bg-white"}`}
            >
              <div className="min-w-0 space-y-1">
                <p
                  className={`truncate text-base font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                >
                  {p.name}
                </p>
                <p
                  className={`break-words text-sm leading-snug ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {p.domain}
                </p>
                {p.interval && (
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Schedule: {p.interval}
                  </p>
                )}
                {p.lastRank !== null && p.lastRank !== undefined && (
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Last Rank: {p.lastRank}
                  </p>
                )}
              </div>

              <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4">
                <span
                  className={`inline-flex w-full justify-center rounded-full px-3 py-1 text-sm font-semibold sm:w-auto ${p.isActive ? "bg-green-100 text-green-700" : isDarkMode ? "bg-gray-600 text-gray-300" : "bg-gray-100 text-gray-600"}`}
                >
                  {p.isActive ? "Active" : "Inactive"}
                </span>

                <button
                  onClick={() => handleToggleSeoCheck(p.id, p.isActive)}
                  className={`flex w-full items-center justify-center gap-2 rounded-full border px-3 py-2 text-sm transition sm:w-auto ${isDarkMode ? "border-gray-600 hover:bg-gray-600" : "border-gray-300 hover:bg-gray-50"}`}
                >
                  {p.isActive ? (
                    <FaToggleOn className="size-6 text-green-500" />
                  ) : (
                    <FaToggleOff
                      className={`size-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                    />
                  )}
                  <span
                    className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                  >
                    {p.isActive ? "Deactivate" : "Start SEO Check"}
                  </span>
                </button>
              </div>
            </div>
          ))}
          {projects.length === 0 && (
            <div
              className={`text-center py-6 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              No projects found.
            </div>
          )}
        </div>
      )}
    </div></>
  );
}
